import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const ContentModerationLayer = () => {
    const { auth } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [contentDetails, setContentDetails] = useState(null);
    const [contentLoading, setContentLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('venue');

    const fetchReports = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/reports`);
            if (!response.ok) throw new Error('Failed to fetch reports');
            const data = await response.json();
            setReports(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleAction = async (id, action) => {
        try {
            const note = notes[id] || '';
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/action`, {
                method: 'POST',
                body: JSON.stringify({ id, action, note }),
            });
            if (response.ok) {
                fetchReports();
                setNotes(prev => ({ ...prev, [id]: '' }));
            }
        } catch (error) {
            console.error('Error performing action:', error);
        }
    };

    const handleNoteChange = (id, value) => {
        setNotes(prev => ({ ...prev, [id]: value }));
    };

    const handleShowEvidence = async (report) => {
        setSelectedReport(report);
        setShowModal(true);
        setContentLoading(true);
        setContentDetails(null);

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/moderation/content/${report.reported_type}/${report.reported_id}`);
            if (response.ok) {
                const data = await response.json();
                setContentDetails(data);
            }
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setContentLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReport(null);
        setContentDetails(null);
    };

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${config.API_BASE_URL}/${path.replace(/^\//, '')}`;
    };

    const renderTable = (type) => {
        const filteredReports = reports.filter(r => r.reported_type === type);
        
        if (filteredReports.length === 0) {
            return (
                <div className="hs-empty-state py-40">
                    <div className="hs-empty-state-icon">
                        <Icon icon="solar:shield-check-bold-duotone" />
                    </div>
                    <div className="hs-empty-state-title">No reports found</div>
                    <div className="hs-empty-state-text">
                        There are currently no flagged or pending reports for this category.
                    </div>
                </div>
            );
        }

        return (
            <div className="hs-table-wrapper">
                <table className="hs-table">
                    <thead>
                        <tr>
                            <th>Reported Item</th>
                            <th>Reporter</th>
                            <th>Reason</th>
                            <th>Evidence</th>
                            <th className="text-center">Status</th>
                            <th>Admin Notes</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredReports.map(report => (
                            <tr key={report.id}>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="w-32-px h-32-px rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary">
                                            <Icon icon={
                                                type === 'venue' ? 'solar:buildings-bold' :
                                                type === 'review' ? 'solar:chat-round-dots-bold' :
                                                type === 'blog' ? 'solar:document-text-bold' :
                                                'solar:gallery-bold'
                                            } />
                                        </div>
                                        <div>
                                            <h6 className="text-sm mb-0 fw-semibold text-dark">
                                                {type === 'venue' ? `Venue #${report.reported_id}` : 
                                                 type === 'review' ? `Review #${report.reported_id}` : 
                                                 type === 'blog' ? `Blog Post #${report.reported_id}` :
                                                 `Image Asset #${report.reported_id}`}
                                            </h6>
                                            <span className="text-xs text-secondary-light">ID: {report.reported_id}</span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center gap-1 text-sm text-dark">
                                        <Icon icon="solar:user-bold" className="text-secondary-light" />
                                        <span>{report.reporter_name || `User #${report.reporter_id}`}</span>
                                    </div>
                                </td>
                                <td>
                                    <span className="text-sm text-dark fw-medium">{report.reason}</span>
                                </td>
                                <td>
                                    <button 
                                        type="button"
                                        className="hs-btn-secondary hs-btn-sm d-inline-flex align-items-center gap-1"
                                        onClick={() => handleShowEvidence(report)}
                                    >
                                        <Icon icon="solar:eye-bold" className="text-primary-600" />
                                        <span>View</span>
                                    </button>
                                </td>
                                <td className="text-center">
                                    <span className={`hs-badge ${
                                        report.status === 'pending' ? 'hs-badge-pending' : 
                                        report.status === 'resolved' ? 'hs-badge-active' : 'hs-badge-rejected'
                                    }`}>
                                        <span className="hs-badge-dot"></span>
                                        {report.status ? report.status.toUpperCase() : 'PENDING'}
                                    </span>
                                </td>
                                <td>
                                    {report.status === 'pending' ? (
                                        <input 
                                            type="text" 
                                            className="hs-input form-control-sm py-1 px-2 text-xs" 
                                            placeholder="Resolution note..." 
                                            value={notes[report.id] || ''}
                                            onChange={(e) => handleNoteChange(report.id, e.target.value)}
                                        />
                                    ) : (
                                        <span className="text-xs text-secondary">{report.admin_notes || '—'}</span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <div className="d-flex align-items-center gap-1 justify-content-center">
                                        <button 
                                            type="button"
                                            disabled={report.status !== 'pending'}
                                            onClick={() => handleAction(report.id, 'dismiss')} 
                                            className="btn btn-sm btn-outline-success radius-8 px-2 py-1 text-xs fw-semibold"
                                            style={report.status !== 'pending' ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            title="Dismiss report without taking action"
                                        >
                                            Dismiss
                                        </button>
                                        <button 
                                            type="button"
                                            disabled={report.status !== 'pending'}
                                            onClick={() => handleAction(report.id, 'remove')} 
                                            className="btn btn-sm btn-outline-danger radius-8 px-2 py-1 text-xs fw-semibold"
                                            style={report.status !== 'pending' ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            title="Delete or take down reported content"
                                        >
                                            Remove
                                        </button>
                                        <button 
                                            type="button"
                                            disabled={report.status !== 'pending'}
                                            onClick={() => handleAction(report.id, 'ban')} 
                                            className="btn btn-sm btn-danger radius-8 px-2 py-1 text-xs fw-semibold"
                                            style={report.status !== 'pending' ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                                            title="Ban the author of this content"
                                        >
                                            Ban Author
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const getCountForType = (type) => reports.filter(r => r.reported_type === type && r.status === 'pending').length;

    return (
        <div className="d-flex flex-column gap-4">
            {/* Top Summary Banner */}
            <div className="hs-card">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-48-px h-48-px rounded-12 bg-warning-50 text-warning-600 d-flex align-items-center justify-content-center border border-warning-100">
                            <Icon icon="solar:shield-warning-bold-duotone" className="text-2xl" />
                        </div>
                        <div>
                            <h5 className="text-lg fw-bold text-dark mb-0">Content Moderation & Safety</h5>
                            <p className="text-sm text-secondary-light mb-0">
                                Review flagged content, user complaints, and take actions to maintain community guidelines.
                            </p>
                        </div>
                    </div>

                    <button 
                        type="button" 
                        className="hs-btn-secondary hs-btn-sm d-flex align-items-center gap-2"
                        onClick={fetchReports}
                    >
                        <Icon icon="solar:refresh-bold" />
                        <span>Refresh Queue</span>
                    </button>
                </div>
            </div>

            {/* Moderation Queue Card */}
            <div className="hs-card p-0">
                <div className="p-20 border-bottom d-flex align-items-center flex-wrap gap-3 justify-content-between bg-white radius-top-12">
                    {/* Navigation Pills */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {[
                            { key: 'venue', label: 'Reported Venues' },
                            { key: 'review', label: 'Reviews' },
                            { key: 'blog', label: 'Blogs & Posts' },
                            { key: 'image', label: 'Images & Media' }
                        ].map((tab) => {
                            const count = getCountForType(tab.key);
                            const isActive = activeTab === tab.key;
                            return (
                                <button 
                                    key={tab.key}
                                    type="button"
                                    className={`btn btn-sm radius-8 px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 ${
                                        isActive ? 'text-white' : 'bg-white text-dark'
                                    }`}
                                    style={{
                                        backgroundColor: isActive ? '#0388d6' : '#ffffff',
                                        borderColor: isActive ? '#0388d6' : '#e2e8f0',
                                        boxShadow: isActive ? '0 2px 6px rgba(3, 136, 214, 0.25)' : 'none'
                                    }}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    <span>{tab.label}</span>
                                    {count > 0 && (
                                        <span className={`badge rounded-pill ${isActive ? 'bg-white text-primary-600' : 'bg-danger-600 text-white'} text-xs px-2`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <span className="text-xs text-secondary-light">
                        Showing reports for: <strong className="text-dark text-capitalize">{activeTab}</strong>
                    </span>
                </div>

                <div className="p-24">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading queue...</span>
                            </div>
                            <p className="text-secondary mt-2">Loading reported items...</p>
                        </div>
                    ) : (
                        renderTable(activeTab)
                    )}
                </div>
            </div>

            {/* Evidence Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-36-px h-36-px rounded-circle bg-warning-50 text-warning-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:shield-warning-bold" className="text-xl" />
                        </div>
                        <div>
                            <Modal.Title className="text-lg fw-bold text-dark mb-0">Report Investigation</Modal.Title>
                            <span className="text-xs text-secondary-light">Verify evidence and inspect content details</span>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-24">
                    {contentLoading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading evidence...</span>
                            </div>
                            <span className="text-secondary mt-2 text-sm">Fetching content snapshot...</span>
                        </div>
                    ) : selectedReport && (
                        <div className="d-flex flex-column gap-3">
                            {/* Meta Info Grid */}
                            <div className="row g-3">
                                <div className="col-sm-6">
                                    <div className="p-12 bg-light rounded-3">
                                        <span className="text-xs text-secondary-light d-block mb-1">Reporter</span>
                                        <div className="fw-semibold text-dark text-sm">
                                            {selectedReport.reporter_name || `User #${selectedReport.reporter_id}`}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="p-12 bg-light rounded-3">
                                        <span className="text-xs text-secondary-light d-block mb-1">Reported Item</span>
                                        <div className="fw-semibold text-dark text-sm text-capitalize">
                                            {selectedReport.reported_type} #{selectedReport.reported_id}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Reason */}
                            <div className="p-16 border rounded-3 bg-white">
                                <span className="text-xs fw-bold text-dark d-block mb-1">Reason for Report:</span>
                                <p className="text-sm text-dark mb-0">{selectedReport.reason}</p>
                            </div>
                            
                            {/* Content Preview */}
                            {contentDetails && (
                                <div className="p-16 border rounded-3 bg-light">
                                    <span className="text-xs fw-bold text-dark d-block mb-2">Content Preview Snapshot:</span>
                                    
                                    {selectedReport.reported_type === 'blog' && (
                                        <div>
                                            {contentDetails.featured_image && (
                                                <img 
                                                    src={getImageUrl(contentDetails.featured_image)} 
                                                    alt="Blog Cover" 
                                                    className="img-fluid rounded mb-3 border" 
                                                    style={{ maxHeight: '200px', objectFit: 'cover', width: '100%' }}
                                                />
                                            )}
                                            <h6 className="fw-bold text-dark mb-1">{contentDetails.title}</h6>
                                            <p className="text-sm text-secondary mb-2">{contentDetails.excerpt}</p>
                                            <div className="text-sm bg-white p-3 border rounded-3 text-secondary" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                                {contentDetails.content}
                                            </div>
                                        </div>
                                    )}

                                    {selectedReport.reported_type === 'review' && (
                                        <div>
                                            <div className="d-flex align-items-center gap-1 mb-2">
                                                <Icon icon="solar:star-bold" className="text-warning-main" />
                                                <strong className="text-dark">{contentDetails.rating} / 5</strong>
                                            </div>
                                            <div className="bg-white p-3 border rounded-3 fst-italic text-sm text-dark">
                                                "{contentDetails.comment}"
                                            </div>
                                        </div>
                                    )}

                                    {selectedReport.reported_type === 'venue' && (
                                        <div>
                                            <h6 className="fw-bold text-dark mb-1">{contentDetails.business_name}</h6>
                                            <p className="text-sm text-secondary-light mb-1">{contentDetails.address}</p>
                                            <p className="text-sm text-dark mb-0">{contentDetails.description}</p>
                                        </div>
                                    )}
                                    
                                    {selectedReport.reported_type === 'image' && (
                                        <div className="text-center">
                                            <img 
                                                src={getImageUrl(contentDetails.image_url)} 
                                                alt="Reported Content" 
                                                className="img-fluid rounded border" 
                                                style={{ maxHeight: '300px' }}
                                            />
                                            {contentDetails.caption && <p className="text-secondary mt-2 text-sm">{contentDetails.caption}</p>}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedReport.evidence_url && (
                                <div className="p-16 border rounded-3 bg-white">
                                    <span className="text-xs fw-bold text-dark d-block mb-1">External Evidence URL:</span>
                                    <a 
                                        href={selectedReport.evidence_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-primary-600 text-sm text-break text-decoration-none fw-medium d-inline-flex align-items-center gap-1"
                                    >
                                        <Icon icon="solar:link-bold" />
                                        <span>{selectedReport.evidence_url}</span>
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-top bg-white py-12 px-24">
                    <Button variant="secondary" className="radius-8" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ContentModerationLayer;
